// Seeded RNG. mulberry32 — fast, decent distribution, deterministic.
// All sim-layer randomness MUST go through this. Math.random is banned
// in src/sim/** by ESLint to keep replays / smoke runs deterministic.

export type Rng = () => number;

/** Build a mulberry32 RNG from a 32-bit integer seed. */
export function makeRng(seed: number): Rng {
  // eslint-disable-next-line no-bitwise
  let a = seed | 0;
  return function next(): number {
    // eslint-disable-next-line no-bitwise
    a = (a + 0x6d2b79f5) | 0;
    // eslint-disable-next-line no-bitwise
    let t = a;
    // eslint-disable-next-line no-bitwise
    t = Math.imul(t ^ (t >>> 15), t | 1);
    // eslint-disable-next-line no-bitwise
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    // eslint-disable-next-line no-bitwise
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Integer in [min, max] (inclusive). */
export function randInt(rng: Rng, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

/** Float in [min, max). */
export function randFloat(rng: Rng, min: number, max: number): number {
  return rng() * (max - min) + min;
}

/** True with probability p. */
export function chance(rng: Rng, p: number): boolean {
  return rng() < p;
}

/** Pick one element uniformly at random. Caller ensures arr.length > 0. */
export function pick<T>(rng: Rng, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length)]!;
}

/** Derive a child seed from a parent seed + label. Lets a system get its
 *  own deterministic stream without coupling to others. */
export function deriveSeed(parent: number, label: string): number {
  let h = parent | 0;
  for (let i = 0; i < label.length; i++) {
    // eslint-disable-next-line no-bitwise
    h = Math.imul(h ^ label.charCodeAt(i), 0x01000193);
  }
  // eslint-disable-next-line no-bitwise
  return h | 0;
}
